import React, {useCallback, useState} from 'react';
import {AccentColorOptions, accentColors, useBreakpointUp} from 'epic-ue-theme';
import SlickWrapper from 'epic-react-common/src/components/SlickWrapper';
import {
    CmsComponentMapper,
    CmsDynamicArray,
    CreatorCard,
    CreatorImageCardProps,
    CreatorVideoCardProps
} from '../../elements';
import {useCommunityActiveIndex, useCommunitySpotlightImage} from './CommunityGallery.hooks';

interface CreatorImageCardWrapProps {
    imageCards?: CreatorImageCardProps[];
    accentColor?: AccentColorOptions;
}

interface CreatorVideoCardWrapProps {
    videoCards?: CreatorVideoCardProps[];
    accentColor?: AccentColorOptions;
}

export type CommunityGalleryCardOptions = CreatorImageCardWrapProps | CreatorVideoCardWrapProps;

export type CommunityGalleryCardOptionsArray = CmsDynamicArray<CommunityGalleryCardOptions>;

export interface CommunityGalleryCardsProps {
    dynamicPageDataArray: CommunityGalleryCardOptionsArray;
    accentColor?: AccentColorOptions;
}

const GetResponsiveCards = (cardsArray): JSX.Element => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const afterChange = useCallback(
        (slide) => {
            setCurrentSlide(slide);
        },
        [setCurrentSlide]
    );
    const isMdUp = useBreakpointUp('md');
    const settings = {
        infinite: false,
        dots: true,
        arrows: false,
        variableWidth: true,
        afterChange
    };

    let responsiveCards: JSX.Element = <div key="no-slider-items" />;
    if (isMdUp) {
        responsiveCards = <>{cardsArray}</>;
    } else {
        responsiveCards = (
            <SlickWrapper className="creator-cards" options={settings}>
                {React.Children.map(cardsArray, (child, index) =>
                    React.cloneElement(child, {
                        notActiveSlide: index !== currentSlide,
                        ...child.props
                    })
                )}
            </SlickWrapper>
        );
    }
    return responsiveCards;
};

const CreatorImageCardsWrap = (props: CreatorImageCardWrapProps): JSX.Element => {
    const {accentColor, imageCards} = props;

    const {setCommunitySpotlightImage} = useCommunitySpotlightImage();

    const cards =
        imageCards &&
        imageCards.map((card, index) => {
            if (index === 0) {
                card.image && setCommunitySpotlightImage(card.image);
            }
            return (
                <CreatorCard
                    key={`${card.title}-${index}`}
                    accentColor={accentColor}
                    eyebrow={card.eyebrow}
                    image={card.image}
                    title={card.title}
                    description={card.description}
                    href={card.href}
                    color={index % 2 === 0 ? 'pearl' : 'chalk'}
                />
            );
        });

    return GetResponsiveCards(cards);
};

const CreatorVideoCardsWrap = (props: CreatorVideoCardWrapProps): JSX.Element => {
    const {accentColor, videoCards} = props;

    const {communityActiveIndex, setCommunityActiveIndex} = useCommunityActiveIndex();

    const handleChange = useCallback(
        (newActiveIndex) => {
            setCommunityActiveIndex(newActiveIndex);
        },
        [setCommunityActiveIndex]
    );

    const cards =
        videoCards &&
        videoCards.map((card, index) => {
            return (
                <CreatorCard
                    key={`${card.title}-${index}`}
                    accentColor={accentColor ? accentColor : accentColors[index]}
                    image={card.image}
                    video={card.video}
                    title={card.title}
                    description={card.description}
                    href={card.href}
                    buttonText={card.buttonText}
                    color={index % 2 === 0 ? 'chalk' : 'pearl'}
                    onChange={() => handleChange(index)}
                    cardIndex={index}
                    active={index === communityActiveIndex}
                />
            );
        });

    return GetResponsiveCards(cards);
};

export const CommunityGalleryCards = (props: CommunityGalleryCardsProps): JSX.Element => {
    const {accentColor, dynamicPageDataArray} = props;

    return (
        <CmsComponentMapper
            dynamicPageDataArray={dynamicPageDataArray}
            elementMapping={{
                imageWrap: <CreatorImageCardsWrap accentColor={accentColor} />,
                videoWrap: <CreatorVideoCardsWrap accentColor={accentColor} />
            }}
        />
    );
};
